/**
 * Passkey ceremony UI — @simplewebauthn/browser (plan §6.8 · demos chrome).
 * Mode-driven: authenticate-only, bootstrap-first, or auto (authenticate then
 * closed-door bootstrap). Token stays httpOnly.
 */
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';
import { useCallback, useEffect, useRef, useState, type JSX, type MutableRefObject } from 'react';

import type { AppConfig, PasskeyMode } from '@/config';

import { useAuth, type SessionProbe } from './auth-context';
import {
  AuthFetchError,
  AuthNetworkError,
  postAuthJson,
  reportProgrammingError,
} from './auth-fetch';
import { isLocalAuthDisabled, LOCAL_AUTH_BYPASS_COPY } from './local-bypass';
import styles from './passkey-login.module.css';
import { resolveReturnTo, type ReturnToLocation } from './return-to';
import { unwrapOptionsEnvelope } from './webauthn-options';

/**
 * Server bounds, mirrored for a PRE-FLIGHT (apps/api `HANDLE_MAX`/`DISPLAY_MAX`).
 *
 * Not defence — the server is the authority — but ordering. The server checks
 * identity at bootstrap/VERIFY, which runs *after* the authenticator ceremony
 * has completed and after the single-use challenge is consumed. Without a
 * pre-flight the user completes a full biometric registration, the device
 * stores a discoverable credential, and only then do they get a generic
 * failure — leaving an orphaned credential and needing a fresh options round
 * trip to retry. `bootstrap` is a shipped production value (AD-15), so this is
 * reachable, not theoretical. The numbers cannot be imported: `web → api` is a
 * forbidden edge, so they are restated with the comparison the server makes.
 */
const HANDLE_MAX_LENGTH = 64;
const DISPLAY_NAME_MAX_LENGTH = 128;

export const PASSKEY_LOGIN_VARIANTS = ['page', 'embed'] as const;
export type PasskeyLoginVariant = (typeof PASSKEY_LOGIN_VARIANTS)[number];

export interface PasskeyLoginProps {
  readonly config: AppConfig;
  readonly mode?: PasskeyMode;
  /**
   * `'page'` owns its chrome — the h1, the lede and the local-mode bypass.
   * `'embed'` renders none of them because `LoginPanel` already has: a second
   * h1 inside the composed panel is an a11y failure, and a second bypass status
   * region is the duplicate the composition test forbids.
   */
  readonly variant?: PasskeyLoginVariant;
  readonly returnTo?: ReturnToLocation | null;
  readonly onSuccess?: (returnTo: ReturnToLocation) => void;
}

/*
 * No 'rejected' member: `classifyError` is the only producer and never
 * returned one, so it was an unreachable variant promising a distinction the
 * UI never made. A server rejection classifies as 'unknown' today. Giving it
 * its own message is a deliberate UX change, not a cleanup — it would need a
 * decision about what to tell a user whose ceremony the server refused.
 *
 * NOTE ON DIAGNOSTICS. This surface reports through `reportProgrammingError`,
 * not `password-login.tsx`'s `reportUnexpected`, and the difference is which
 * direction the predicate runs. Excluding known-expected failures is sound only
 * where that set is CLOSED; the ceremony's is not, because
 * `@simplewebauthn/browser` wraps platform failures in a `WebAuthnError`
 * carrying any DOMException name and the platform can raise bare DOMExceptions
 * besides. So the closed set is taken from the other side — TypeError,
 * ReferenceError and SyntaxError are never legitimate ceremony outcomes and are
 * always defects of ours. See the two helpers in `auth-fetch.ts`.
 */
type CeremonyError =
  | 'cancelled'
  | 'network'
  | 'unsupported'
  | 'rate-limited'
  | 'no-credential-path'
  | 'handle-too-long'
  | 'display-name-too-long'
  | 'unknown';

const RATE_LIMIT_STATUS = 429;

function classifyError(err: unknown): CeremonyError {
  /* Typed first: helper POSTs raise AuthNetworkError, not a raw TypeError. */
  if (err instanceof AuthNetworkError) return 'network';
  if (err instanceof NoCredentialPathError) return 'no-credential-path';
  if (err instanceof WebAuthnUnsupportedError) return 'unsupported';
  /*
   * The shared ADR-0011 consecutive-failure ban answers 429 on the ceremony
   * route too (apps/api `sendBannedAuthentication`), and the password form on
   * the same panel already renders a distinct message for that exact state.
   * Folding it into 'unknown' told half the panel to "Try again" against a path
   * banned for five minutes. Read off the TAGGED status, never a message.
   */
  if (err instanceof CeremonyStageError && err.status === RATE_LIMIT_STATUS) return 'rate-limited';
  if (err instanceof Error) {
    if (err.name === 'NotAllowedError') return 'cancelled';
    if (err.name === 'NotSupportedError') return 'unsupported';
  }
  return 'unknown';
}

/**
 * Raised by the pre-check, NOT matched off the library's message string.
 *
 * `startAuthentication`/`startRegistration` throw a PLAIN `Error` whose message
 * is 'WebAuthn is not supported in this browser' when `browserSupportsWebAuthn()`
 * is false — the common case being any non-secure context, such as an `http://`
 * LAN dev server, not merely an ancient browser. A plain Error has `name ===
 * 'Error'`, so it fell through to 'unknown' and the user was told to retry
 * something the context can never satisfy. The genuine `NotSupportedError`
 * DOMException that `classifyError` already handled is far rarer.
 */
class WebAuthnUnsupportedError extends Error {
  constructor() {
    super('WebAuthn is not available in this browser or context.');
    this.name = 'WebAuthnUnsupportedError';
  }
}

/**
 * Both credential doors answered a closed-door 401 — there is no way in.
 *
 * Distinct from 'unknown' because the generic copy's only remedy is "Try
 * again", and retrying is the one thing that cannot help: the deployment has
 * neither an enrolled credential nor an open enrolment route. This discloses
 * nothing new — the client issued both requests and already observed both 401s
 * — so security.md §3's uniformity requirement is untouched.
 */
class NoCredentialPathError extends Error {
  constructor(cause: unknown) {
    super('No credential path is available on this deployment.', { cause });
    this.name = 'NoCredentialPathError';
  }
}

/**
 * Which leg of a ceremony rejected, and with what status (AD-9 · plan §2.2.1).
 *
 * The stage is tagged rather than the reason because there IS no reason on the
 * wire: every closed-door 401 body is byte-identical by design (security §3),
 * so `no_user`, `bootstrap_closed`, a revoked session and a cloned credential
 * are indistinguishable to the client. The stage, in contrast, is knowable
 * locally — it is simply which request we had issued.
 *
 * It deliberately does NOT set a recognised `name`, so `classifyError` keeps
 * folding it into 'unknown'. That is what makes a tagged 429 or 5xx render the
 * same "Sign-in failed. Try again." copy it rendered before this type existed:
 * the tag exists for the fallback predicate, not for the user-facing message.
 */
class CeremonyStageError extends Error {
  readonly stage: 'options' | 'verify';
  readonly status: number;

  /* `cause` keeps the originating AuthFetchError — and with it the envelope's
     `error.code` and body — reachable. It is never rendered, so the uniform-401
     guarantee is untouched; without it the wrapper discarded the very data
     AuthFetchError exists to carry, one layer above where it was produced. */
  constructor(stage: 'options' | 'verify', status: number, cause: unknown) {
    super(`Ceremony ${stage} stage rejected with status ${String(status)}`, { cause });
    this.name = 'CeremonyStageError';
    this.stage = stage;
    this.status = status;
  }
}

/** A ceremony could not start: the options envelope carried no `data` record. */
class CeremonyOptionsError extends Error {
  constructor() {
    super('Ceremony options payload was missing its data record.');
    this.name = 'CeremonyOptionsError';
  }
}

/**
 * The ONLY condition that may switch ceremony paths.
 *
 * Narrow because the ban budget is asymmetric, not because narrow is tidier.
 * The two closed-door options legs — `authenticateOptions`/`no_user` and
 * `bootstrapOptions`/`bootstrap_closed` — return through `fail()`, which does
 * not call `recordFailure`, so retrying the other path there costs nothing.
 * A 429, a 5xx, a cancelled prompt or ANY verify failure either already spent
 * ban budget or is a condition the operator has to see; retrying past those
 * would burn the shared 3-strike IP ban that also locks out passkey sign-in.
 */
function isClosedDoor(err: unknown): boolean {
  return err instanceof CeremonyStageError && err.stage === 'options' && err.status === 401;
}

/** Tag an `AuthFetchError` with the leg it came from; leave everything else alone. */
async function ceremonyPost(
  stage: 'options' | 'verify',
  config: AppConfig,
  path: string,
  body?: unknown,
): Promise<unknown> {
  try {
    return await postAuthJson(config, path, body);
  } catch (err) {
    /* AuthNetworkError passes through UNTAGGED on purpose: `fetch` never
       reached a door, so there is no closed one to route around, and
       `classifyError` must still see it to report 'network'. */
    if (err instanceof AuthFetchError) throw new CeremonyStageError(stage, err.status, err);
    throw err;
  }
}

/**
 * Unwrap the `ApiResponse` envelope around a WebAuthn options payload.
 *
 * The rule itself lives in `webauthn-options.ts` — both credential surfaces
 * need it and it carries no taxonomy. `CeremonyOptionsError` stays private to
 * this module and is handed over as a factory, so sharing the unwrap does not
 * make either surface's error classification a cross-component contract.
 */
function ceremonyOptions(payload: unknown): unknown {
  return unwrapOptionsEnvelope(payload, () => new CeremonyOptionsError());
}

async function authenticate(config: AppConfig): Promise<void> {
  const options = await ceremonyPost('options', config, 'authenticate/options');
  const assertion = await startAuthentication({
    optionsJSON: ceremonyOptions(options) as Parameters<
      typeof startAuthentication
    >[0]['optionsJSON'],
  });
  await ceremonyPost('verify', config, 'authenticate/verify', { response: assertion });
}

async function bootstrap(config: AppConfig, handle: string, displayName: string): Promise<void> {
  const options = await ceremonyPost('options', config, 'bootstrap/options');
  const attestation = await startRegistration({
    optionsJSON: ceremonyOptions(options) as Parameters<typeof startRegistration>[0]['optionsJSON'],
  });
  await ceremonyPost('verify', config, 'bootstrap/verify', {
    handle,
    displayName,
    nickname: 'Primary',
    response: attestation,
  });
}

/**
 * Dispatch, with the AD-9 stage-aware fallback.
 *
 * `mode` still drives RENDERING exactly as before — this function is the only
 * thing that changed. The replaced `auto` arm was a bare `catch { await
 * bootstrap(...) }`, which retried through cancelled prompts, 429s and 5xxs
 * alike; that catch-all is what made a `bootstrap` bundle against
 * `BOOTSTRAP_ENABLED=false` fatal rather than recoverable.
 */
async function runCeremony(
  mode: PasskeyMode,
  config: AppConfig,
  handle: string,
  displayName: string,
): Promise<void> {
  /* Pre-check, so an unsupported browser or a non-secure context is reported as
     such instead of arriving as the library's plain-Error sentinel. */
  if (!browserSupportsWebAuthn()) throw new WebAuthnUnsupportedError();
  if (mode === 'authenticate') {
    /* Authenticate-only in both directions: there is no closed door to route
       around once bootstrap is closed, and enrolment is not a login fallback. */
    await authenticate(config);
    return;
  }
  if (mode === 'bootstrap') {
    try {
      await bootstrap(config, handle, displayName);
    } catch (err) {
      if (!isClosedDoor(err)) throw err;
      /* Enrolment is closed and a user already exists, so signing in is the
         recovery path (login-password-passkey-ui-plan §1179). The typed handle
         and display name are deliberately discarded — they describe a
         registration that did not happen. */
      await runFallbackLeg(async () => {
        await authenticate(config);
      });
    }
    return;
  }
  try {
    await authenticate(config);
  } catch (err) {
    if (!isClosedDoor(err)) throw err;
    await runFallbackLeg(async () => {
      await bootstrap(config, handle, displayName);
    });
  }
}

/**
 * The second leg, after the first met a closed door.
 *
 * A closed door HERE means both doors are shut, which is a different condition
 * from either one alone and needs different copy — otherwise the deployment
 * with no usable sign-in path at all is the one told to "Try again". Anything
 * else propagates unchanged, so a 429 or 5xx on the second leg still classifies
 * on its own terms.
 */
async function runFallbackLeg(leg: () => Promise<void>): Promise<void> {
  try {
    await leg();
  } catch (err) {
    if (isClosedDoor(err)) throw new NoCredentialPathError(err);
    throw err;
  }
}

/** Pre-flight for the enrolment fields — see the MAX_LENGTH constants above. */
function validateIdentity(handle: string, displayName: string): CeremonyError | null {
  /* Empty is NOT an error: the server defaults an absent handle to 'owner'. */
  if (handle.length > HANDLE_MAX_LENGTH) return 'handle-too-long';
  if (displayName.length > DISPLAY_NAME_MAX_LENGTH) return 'display-name-too-long';
  return null;
}

function errorMessage(error: CeremonyError): string {
  if (error === 'cancelled') return 'Passkey prompt was cancelled.';
  if (error === 'unsupported') return 'This browser does not support passkeys.';
  if (error === 'network') return 'Network error - try again.';
  /*
   * No "or sign in with your password" pointer, deliberately: the ADR-0011 ban
   * is per-IP and shared across BOTH credentials, so directing the user at the
   * other surface would recommend a path the same counter has closed. No
   * duration either, for the reason password-login.tsx documents at length —
   * two independent producers return this status and only the wire could say
   * which.
   */
  if (error === 'rate-limited') return 'Too many sign-in attempts. Try again later.';
  if (error === 'no-credential-path') {
    return 'No sign-in method is available on this deployment. Contact the operator.';
  }
  /* Composed from the constant the comparison uses, so the number the user is
     told is the number that would have rejected them. */
  if (error === 'handle-too-long') {
    return `Handle must be at most ${String(HANDLE_MAX_LENGTH)} characters.`;
  }
  if (error === 'display-name-too-long') {
    return `Display name must be at most ${String(DISPLAY_NAME_MAX_LENGTH)} characters.`;
  }
  return 'Sign-in failed. Try again.';
}

export function PasskeyLogin({
  config,
  mode = config.passkeyMode,
  variant = 'page',
  returnTo,
  onSuccess,
}: PasskeyLoginProps): JSX.Element {
  /*
   * The bypass is SKIPPED when embedded, not duplicated. LoginPanel is the
   * sole bypass owner (AD-11), and it renders the ceremony only on the
   * non-bypass path — so reaching this branch as an embed would mean two
   * `role="status"` nodes saying the same thing, which the composition test
   * forbids. Standalone `page` keeps it: rendered on its own, nothing else
   * would say auth is off.
   */
  if (variant !== 'embed' && isLocalAuthDisabled(config)) {
    return (
      <div className={styles['root']} role="status">
        {LOCAL_AUTH_BYPASS_COPY}
      </div>
    );
  }
  /* Both are passed EXPLICITLY, which is why PasskeyCeremonyProps declares them
     required — a second layer of defaults there could never fire. */
  return (
    <PasskeyCeremony
      config={config}
      mode={mode}
      variant={variant}
      returnTo={returnTo ?? null}
      onSuccess={onSuccess}
    />
  );
}

interface CeremonyState {
  readonly busy: boolean;
  readonly error: CeremonyError | null;
  readonly setBusy: (next: boolean) => void;
  readonly setError: (next: CeremonyError | null) => void;
  /**
   * A ref, not the `busy` state, is what makes a double submit one ceremony —
   * the same reasoning password-login.tsx spells out. A guard reading `busy`
   * reads a RENDER closure, and React 18 batches every event in a task, so two
   * clicks in one batch both see `busy === false`. The cost here is worse than
   * a duplicate POST: @simplewebauthn's `WebAuthnAbortService` aborts the first
   * ceremony when a second starts, so the first promise rejects with an
   * AbortError and renders a failure over a ceremony the user never cancelled.
   */
  readonly inFlight: { current: boolean };
  /**
   * A ceremony outlives the screen: it awaits an authenticator prompt the user
   * may take seconds to answer, and they can navigate away meanwhile. Without
   * this, a late resolution still fires `onSuccess` and pulls them back to a
   * route they left. Mirrors the `isCurrent()` predicate in auth-context.tsx.
   */
  readonly mounted: { current: boolean };
}

interface CeremonyRunOpts {
  readonly config: AppConfig;
  readonly mode: PasskeyMode;
  readonly identity: { readonly handle: string; readonly displayName: string };
  readonly onSettled: {
    readonly refresh: () => Promise<SessionProbe>;
    readonly onSuccess: (() => void) | undefined;
  };
}

/**
 * Owns the ceremony's transient state and the submit itself.
 *
 * Extracted from `PasskeyCeremony` for two reasons: the component was over the
 * 50-line limit (style §3), and keeping the two refs INSIDE the hook is what
 * lets `useCallback` name only real dependencies — returning them to the
 * component made every render produce fresh bindings that
 * `react-hooks/exhaustive-deps` could not prove stable.
 */
interface CeremonyOutcome {
  readonly confirmed: boolean;
  readonly error: CeremonyError | null;
}

/**
 * Run the ceremony and CLASSIFY it, without touching component state.
 *
 * Extracted so the hook applies its state once, in one mounted-guarded block:
 * folding the guards back inline pushed the callback past both the 50-line and
 * complexity-10 limits (style §3), and the shape of "compute an outcome, then
 * commit it" is what makes the unmount guard a single decision rather than one
 * per setter.
 *
 * Ownership of the abort singleton is claimed only around the ceremony itself,
 * because `WebAuthnAbortService` is module-level and shared with the
 * add-passkey surface.
 */
async function attemptCeremony(
  run: {
    readonly config: AppConfig;
    readonly mode: PasskeyMode;
    readonly handle: string;
    readonly displayName: string;
    readonly refresh: () => Promise<SessionProbe>;
  },
  ownsCeremony: MutableRefObject<boolean>,
): Promise<CeremonyOutcome> {
  const owns = ownsCeremony;
  owns.current = true;
  try {
    await runCeremony(run.mode, run.config, run.handle, run.displayName).finally(() => {
      owns.current = false;
    });
    /* Was `throw new Error('rejected')`, which classified as 'unknown' and so
       rendered this exact message — returned directly now that the success path
       no longer needs an exception to skip navigation. Same copy, no control
       flow through the catch. */
    const confirmed = (await run.refresh()) === 'authenticated';
    return { confirmed, error: confirmed ? null : 'unknown' };
  } catch (err) {
    const classified = classifyError(err);
    /* The user still sees the classified copy; only a defect of ours escapes,
       asynchronously, after this component's state has settled. */
    reportProgrammingError(err);
    return { confirmed: false, error: classified };
  }
}

function useCeremonySubmit(opts: CeremonyRunOpts): CeremonyState & {
  readonly onSubmit: () => Promise<void>;
} {
  const { config, mode, identity, onSettled } = opts;
  const { handle, displayName } = identity;
  const { refresh, onSuccess } = onSettled;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<CeremonyError | null>(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  /**
   * True only while OUR ceremony is outstanding — the same ownership discipline
   * `add-passkey.tsx` uses, and for the same reason: `WebAuthnAbortService` is a
   * module-level singleton, so an unconditional cancel on unmount would abort a
   * ceremony this component never started.
   *
   * Cancelling at all is the behaviour change. Navigating away from /login
   * mid-ceremony previously left the authenticator prompt live, answering into
   * a component that no longer exists.
   */
  const ownsCeremony = useRef(false);
  useEffect(() => {
    mounted.current = true;
    const owns = ownsCeremony;
    return () => {
      mounted.current = false;
      if (owns.current) WebAuthnAbortService.cancelCeremony();
    };
  }, []);

  const onSubmit = useCallback(async () => {
    if (inFlight.current) return;
    setError(null);
    /* Pre-flight only where the fields are actually rendered and used. */
    if (mode !== 'authenticate') {
      const invalid = validateIdentity(handle, displayName);
      if (invalid !== null) {
        setError(invalid);
        return;
      }
    }
    /* Written BEFORE the first await, so the guard above is already closed when
       a second submit from the same batch reaches it. */
    inFlight.current = true;
    setBusy(true);
    const outcome = await attemptCeremony(
      { config, mode, handle, displayName, refresh },
      ownsCeremony,
    );
    inFlight.current = false;
    /* One guarded write-back instead of a guard per setter: a ceremony outlives
       this component, so a late settle must not write into an unmounted tree. */
    if (mounted.current) {
      setBusy(false);
      if (outcome.error !== null) setError(outcome.error);
    }
    if (!outcome.confirmed || !mounted.current) return;
    /*
     * Navigation sits OUTSIDE the classified block, matching password-login.tsx.
     * Inside it, a router callback that threw was caught by `classifyError` and
     * reported as a failed ceremony — telling a user whose passkey the server
     * had already verified, and whose session exists, that sign-in failed.
     */
    onSuccess?.();
  }, [config, displayName, handle, mode, onSuccess, refresh]);

  return { busy, error, setBusy, setError, inFlight, mounted, onSubmit };
}

/* `mode` and `variant` are REQUIRED: the sole caller resolves both defaults and
   passes them explicitly, so optional copies here were unreachable branches. */
interface PasskeyCeremonyProps {
  readonly config: AppConfig;
  readonly mode: PasskeyMode;
  readonly variant: PasskeyLoginVariant;
  readonly returnTo?: ReturnToLocation | null | undefined;
  readonly onSuccess?: ((returnTo: ReturnToLocation) => void) | undefined;
}

function PasskeyCeremony({
  config,
  mode,
  variant,
  returnTo,
  onSuccess,
}: PasskeyCeremonyProps): JSX.Element {
  const auth = useAuth();
  const [handle, setHandle] = useState('owner');
  const [displayName, setDisplayName] = useState('Owner');
  const notifySuccess = useCallback(() => {
    onSuccess?.(resolveReturnTo(returnTo ?? null));
  }, [onSuccess, returnTo]);
  const { busy, error, onSubmit } = useCeremonySubmit({
    config,
    mode,
    identity: { handle, displayName },
    onSettled: { refresh: auth.refresh, onSuccess: notifySuccess },
  });

  return (
    <PasskeyForm
      {...{
        mode,
        variant,
        busy,
        error,
        handle,
        displayName,
        setHandle,
        setDisplayName,
        onSubmit,
      }}
    />
  );
}

interface PasskeyFormProps {
  readonly mode: NonNullable<PasskeyLoginProps['mode']>;
  readonly variant: NonNullable<PasskeyLoginProps['variant']>;
  readonly busy: boolean;
  readonly error: CeremonyError | null;
  readonly handle: string;
  readonly displayName: string;
  readonly setHandle: (value: string) => void;
  readonly setDisplayName: (value: string) => void;
  readonly onSubmit: () => Promise<void>;
}

function PasskeyForm({
  mode,
  variant,
  busy,
  error,
  handle,
  displayName,
  setHandle,
  setDisplayName,
  onSubmit,
}: PasskeyFormProps): JSX.Element {
  const isEmbed = variant === 'embed';
  return (
    /*
     * `embed` drops the panel chrome as well as the copy: nested inside
     * LoginPanel's own panel, a second bordered box reads as a second card.
     *
     * It becomes a NAMED landmark instead. Composed with PasswordLogin's
     * (likewise named) form, that is what gives a screen-reader user a boundary
     * between the two credential surfaces — previously the only separator was
     * LoginPanel's "or" divider, which is `aria-hidden`, leaving Password,
     * Handle and Display name as one undifferentiated run. Standalone `page`
     * needs no label: it is the only thing on the document.
     */
    <div
      className={isEmbed ? styles['embedded'] : styles['root']}
      {...(isEmbed ? { role: 'group', 'aria-label': 'Passkey sign-in' } : {})}
    >
      {isEmbed ? null : (
        <>
          <h1 className={styles['title']}>Meridian</h1>
          <p className={styles['lede']}>Sign in with a passkey. No password.</p>
        </>
      )}
      {mode !== 'authenticate' ? (
        <PasskeyFields {...{ handle, displayName, setHandle, setDisplayName }} />
      ) : null}
      <button
        type="button"
        className={styles['button']}
        disabled={busy}
        onClick={() => void onSubmit()}
      >
        {busy ? 'Waiting for authenticator…' : 'Continue with passkey'}
      </button>
      {error !== null ? (
        <p className={styles['error']} role="alert">
          {errorMessage(error)}
        </p>
      ) : null}
      {/*
       * WCAG 4.1.3 for the pending state, matching password-login.tsx. A
       * disabled button is blurred and dropped from the a11y tree, so the
       * 'Waiting for authenticator…' name change above announces nothing and
       * focus falls to <body>. Without this the composed panel satisfied 4.1.3
       * for one credential path and not the other. Separate from the error
       * alert so a polite status cannot preempt an assertive error.
       */}
      <p className="visually-hidden" role="status">
        {busy ? 'Waiting for authenticator…' : ''}
      </p>
    </div>
  );
}

function PasskeyFields({
  handle,
  displayName,
  setHandle,
  setDisplayName,
}: {
  readonly handle: string;
  readonly displayName: string;
  readonly setHandle: (value: string) => void;
  readonly setDisplayName: (value: string) => void;
}): JSX.Element {
  return (
    <div className={styles['fields']}>
      <label className={styles['label']}>
        Handle
        <input
          className={styles['input']}
          value={handle}
          onChange={(event) => {
            setHandle(event.target.value);
          }}
          maxLength={HANDLE_MAX_LENGTH}
          /*
           * Stays `username` despite colliding with PasswordLogin's AD-2 assist
           * under `auto`. The collision is real but CONTAINED: production is
           * `authenticate` (no handle field) or `bootstrap` (no password form,
           * so no assist), and only non-production `auto` renders both. The
           * composition test pins that containment deliberately — see
           * 'keeps exactly one accessible-tree username field under auto'.
           * Changing this to 'off' is a live proposal, not a cleanup: it needs
           * the manual password-manager check §2.2.8 defines as the only thing
           * that can actually confirm autofill behaviour.
           */
          autoComplete="username"
        />
      </label>
      <label className={styles['label']}>
        Display name
        <input
          className={styles['input']}
          value={displayName}
          onChange={(event) => {
            setDisplayName(event.target.value);
          }}
          maxLength={DISPLAY_NAME_MAX_LENGTH}
          autoComplete="name"
        />
      </label>
    </div>
  );
}
